import { useState, useEffect, useCallback } from 'react';
import { ClipboardCopy, Check, Filter } from 'lucide-react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

interface DemandSource {
  salesOrderId: string;
  orderNumber: number;
  customer: string | null;
  quantity: number;
}

interface DemandItem {
  nodeId: string;
  typeId: number;
  typeName: string;
  iconId: number | null;
  marketGroupId: number | null;
  marketGroupName: string | null;
  grossQuantity: number;
  inStock: number;
  inProduction: number;
  inProcurement: number;
  netQuantity: number;
  isBuild: boolean;
  blueprintId: number | null;
  blueprintActivity: string | null;
  runsNeeded: number | null;
  materialEfficiency: number;
  structureId: string | null;
  structureName: string | null;
  sources: DemandSource[];
}

interface DemandData {
  id: string;
  lastCalculatedAt: string | null;
  demands: DemandItem[];
}

function formatNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }
  return num.toLocaleString();
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function SupplyChain() {
  const [demandData, setDemandData] = useState<DemandData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState<{
    active: boolean;
    phase: 'aggregating' | 'done';
  }>({ active: false, phase: 'aggregating' });
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'demand' | 'shopping'>('demand');
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);
  const [hideCoveredItems, setHideCoveredItems] = useState(false);

  const fetchDemandData = useCallback(async () => {
    try {
      const response = await api.get('/supply-chain');
      setDemandData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load supply chain');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDemandData();
      setLoading(false);
    };
    loadData();
  }, [fetchDemandData]);

  const handleRecalculate = async () => {
    setRecalculating({ active: true, phase: 'aggregating' });
    try {
      await api.post('/supply-chain/recalculate');
      await fetchDemandData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to recalculate demand');
    } finally {
      setRecalculating({ active: false, phase: 'aggregating' });
    }
  };

  // Filter and sort demands: by market group, then by name
  const sortedDemands = demandData?.demands
    ? [...demandData.demands]
        .filter(d => {
          if (!hideCoveredItems) return true;
          // Hide items that are fully covered (netQuantity === 0)
          return d.netQuantity > 0;
        })
        .sort((a, b) => {
          // First sort by market group name
          const groupA = a.marketGroupName ?? '';
          const groupB = b.marketGroupName ?? '';
          if (groupA !== groupB) {
            // Items without market group go to the end
            if (!groupA) return 1;
            if (!groupB) return -1;
            return groupA.localeCompare(groupB);
          }
          // Then by item name
          return a.typeName.localeCompare(b.typeName);
        })
    : [];

  // Shopping list: only items that are NOT build (isBuild = false) and have net need
  const shoppingList = demandData?.demands
    ? demandData.demands
        .filter(d => !d.isBuild && d.netQuantity > 0)
        .sort((a, b) => {
          // First sort by market group name
          const groupA = a.marketGroupName ?? '';
          const groupB = b.marketGroupName ?? '';
          if (groupA !== groupB) {
            if (!groupA) return 1;
            if (!groupB) return -1;
            return groupA.localeCompare(groupB);
          }
          return a.typeName.localeCompare(b.typeName);
        })
    : [];

  // Export shopping list to clipboard in EVE multibuy format
  const handleExportToClipboard = useCallback(async () => {
    if (shoppingList.length === 0) return;

    // EVE multibuy format: "ItemName Quantity" per line
    const multibuyText = shoppingList
      .map(item => `${item.typeName} ${item.netQuantity}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(multibuyText);
      setShowCopiedNotification(true);
      setTimeout(() => setShowCopiedNotification(false), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, [shoppingList]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400">Loading supply chain...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Recalculate Progress Modal */}
      {recalculating.active && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 p-8 max-w-md w-full mx-4">
            <div className="flex flex-col items-center text-center">
              {/* Animated Icon */}
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full border-4 border-blue-500/30 flex items-center justify-center">
                  <svg className="w-10 h-10 text-blue-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                {/* Spinning ring */}
                <div className="absolute inset-0 w-20 h-20">
                  <svg className="w-full h-full animate-spin" viewBox="0 0 80 80">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      className="text-blue-500"
                      strokeDasharray="60 180"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-white mb-2">Recalculating Supply Chain</h2>

              {/* Phase indicator */}
              <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                <div className="h-2 rounded-full bg-blue-500 w-full animate-pulse" />
              </div>

              {/* Status text */}
              <p className="text-gray-300 text-sm">
                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse" />
                Aggregating demand from sales orders...
              </p>

              {/* Warning text */}
              <p className="text-yellow-500/70 text-xs mt-4">
                Please wait, do not close this page
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="h-full flex flex-col overflow-hidden p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Supply Chain</h1>
            <p className="text-sm text-gray-400">
              Aggregated demand from all accepted sales orders
              {demandData?.lastCalculatedAt && (
                <span className="ml-2">
                  (Last updated: {formatTimeAgo(demandData.lastCalculatedAt)})
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setHideCoveredItems(!hideCoveredItems)}
              className={`flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                hideCoveredItems
                  ? 'bg-green-600 hover:bg-green-500 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
              title={hideCoveredItems ? 'Show all items' : 'Hide covered items'}
            >
              <Filter className="w-4 h-4" />
              {hideCoveredItems ? 'Showing Uncovered' : 'Hide Covered'}
            </button>
            <button
              onClick={handleRecalculate}
              disabled={recalculating.active}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white rounded text-sm"
            >
              {recalculating.active ? 'Recalculating...' : 'Recalculate from Sales Orders'}
            </button>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded text-red-300 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              ✕
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-700 mb-4">
          <button
            onClick={() => setActiveTab('demand')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'demand'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            All Demand
            {demandData && (
              <span className="ml-2 text-xs text-gray-500">
                ({demandData.demands.length} items)
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('shopping')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'shopping'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Shopping List
            <span className="ml-2 text-xs text-gray-500">
              ({shoppingList.length} items)
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'demand' && (
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              {/* Summary stats */}
              {demandData && demandData.demands.length > 0 && (
                <div className="p-4 border-b border-gray-700 bg-gray-750 flex gap-6">
                  <div>
                    <span className="text-gray-400 text-sm">Total Items:</span>
                    <span className="text-white font-medium ml-2">{demandData.demands.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Items with Net Need:</span>
                    <span className="text-yellow-400 font-medium ml-2">
                      {demandData.demands.filter(d => d.netQuantity > 0).length}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Build:</span>
                    <span className="text-blue-400 font-medium ml-2">
                      {demandData.demands.filter(d => d.isBuild).length}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Buy:</span>
                    <span className="text-gray-300 font-medium ml-2">
                      {demandData.demands.filter(d => !d.isBuild).length}
                    </span>
                  </div>
                </div>
              )}

              {/* Table header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-700 bg-gray-800 text-sm text-gray-400 font-medium sticky top-0 z-10">
                <div className="w-40">Market Group</div>
                <div className="w-8"></div>
                <div className="flex-1">Item</div>
                <div className="w-16 text-center">Type</div>
                <div className="text-right w-24">Gross</div>
                <div className="text-right w-24">In Stock</div>
                <div className="text-right w-24">In Prod</div>
                <div className="text-right w-24">In Proc.</div>
                <div className="text-right w-24">Net Need</div>
              </div>

              {/* Table content */}
              {!demandData || demandData.demands.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No demand data available.</p>
                  <p className="text-sm mt-2">
                    Click "Recalculate from Sales Orders" to aggregate demand from all accepted orders.
                  </p>
                </div>
              ) : (
                sortedDemands.map(item => {
                  const isFulfilled = item.inStock >= item.grossQuantity;
                  const isPartiallyFulfilled = item.inStock + item.inProduction + item.inProcurement >= item.grossQuantity;
                  const rowBgClass = isFulfilled
                    ? 'bg-green-900/20'
                    : isPartiallyFulfilled
                      ? 'bg-yellow-900/20'
                      : '';

                  return (
                    <div
                      key={item.nodeId}
                      className={`flex items-center gap-2 px-4 py-2 border-b border-gray-700 hover:bg-gray-750 ${rowBgClass}`}
                    >
                      <div className="w-40 min-w-0">
                        <span className="text-xs text-gray-400 truncate block" title={item.marketGroupName ?? 'Unknown'}>
                          {item.marketGroupName ?? '-'}
                        </span>
                      </div>
                      <img
                        src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`}
                        alt=""
                        className="w-8 h-8"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-200 truncate block" title={item.typeName}>
                          {item.typeName}
                        </span>
                        {item.structureName && (
                          <span className="text-xs text-gray-500 truncate block">
                            @ {item.structureName}
                          </span>
                        )}
                      </div>
                      <div className="w-16 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${item.isBuild ? 'bg-blue-900/50 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>
                          {item.isBuild ? 'Build' : 'Buy'}
                        </span>
                      </div>
                      <div className="text-right w-24">
                        <span className="text-sm text-gray-300">{formatNumber(item.grossQuantity)}</span>
                      </div>
                      <div className="text-right w-24">
                        <span className={`text-sm ${item.inStock > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                          {formatNumber(item.inStock)}
                        </span>
                      </div>
                      <div className="text-right w-24">
                        <span className={`text-sm ${item.inProduction > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                          {formatNumber(item.inProduction)}
                        </span>
                      </div>
                      <div className="text-right w-24">
                        <span className={`text-sm ${item.inProcurement > 0 ? 'text-purple-400' : 'text-gray-500'}`}>
                          {formatNumber(item.inProcurement)}
                        </span>
                      </div>
                      <div className="text-right w-24">
                        <span className={`text-sm font-medium ${item.netQuantity > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {formatNumber(item.netQuantity)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'shopping' && (
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              {/* Summary */}
              {shoppingList.length > 0 && (
                <div className="p-4 border-b border-gray-700 bg-gray-750">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Items to Buy:</span>
                        <span className="text-white font-medium">{shoppingList.length}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        These are all items marked as "Buy" that still have a net need after accounting for stock and production.
                      </p>
                    </div>
                    <button
                      onClick={handleExportToClipboard}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
                      title="Export to EVE Multibuy"
                    >
                      <ClipboardCopy className="w-4 h-4" />
                      Export Multibuy
                    </button>
                  </div>
                </div>
              )}

              {/* Table header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-700 bg-gray-800 text-sm text-gray-400 font-medium sticky top-0 z-10">
                <div className="w-40">Market Group</div>
                <div className="w-8"></div>
                <div className="flex-1">Item</div>
                <div className="text-right w-24">Gross</div>
                <div className="text-right w-24">In Stock</div>
                <div className="text-right w-24">In Prod</div>
                <div className="text-right w-24">In Proc.</div>
                <div className="text-right w-24">To Buy</div>
              </div>

              {/* Shopping list content */}
              {shoppingList.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No items to buy.</p>
                  <p className="text-sm mt-2">
                    All materials are either in stock, in production, or being built.
                  </p>
                </div>
              ) : (
                shoppingList.map(item => (
                  <div
                    key={item.nodeId}
                    className="flex items-center gap-2 px-4 py-2 border-b border-gray-700 hover:bg-gray-750"
                  >
                    <div className="w-40 min-w-0">
                      <span className="text-xs text-gray-400 truncate block" title={item.marketGroupName ?? 'Unknown'}>
                        {item.marketGroupName ?? '-'}
                      </span>
                    </div>
                    <img
                      src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`}
                      alt=""
                      className="w-8 h-8"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <span className="flex-1 text-sm text-gray-200 truncate" title={item.typeName}>
                      {item.typeName}
                    </span>
                    <div className="text-right w-24">
                      <span className="text-sm text-gray-300">{formatNumber(item.grossQuantity)}</span>
                    </div>
                    <div className="text-right w-24">
                      <span className={`text-sm ${item.inStock > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                        {formatNumber(item.inStock)}
                      </span>
                    </div>
                    <div className="text-right w-24">
                      <span className={`text-sm ${item.inProduction > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                        {formatNumber(item.inProduction)}
                      </span>
                    </div>
                    <div className="text-right w-24">
                      <span className={`text-sm ${item.inProcurement > 0 ? 'text-purple-400' : 'text-gray-500'}`}>
                        {formatNumber(item.inProcurement)}
                      </span>
                    </div>
                    <div className="text-right w-24">
                      <span className="text-sm font-medium text-yellow-400">
                        {formatNumber(item.netQuantity)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copied to clipboard notification */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          showCopiedNotification
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-3 px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg">
          <div className="flex items-center justify-center w-6 h-6 bg-white/20 rounded-full">
            <Check className="w-4 h-4" />
          </div>
          <div>
            <p className="font-medium">Copied to clipboard!</p>
            <p className="text-sm text-green-100">{shoppingList.length} items ready for EVE Multibuy</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SupplyChain;
